import type { BeatSlot, Measure, NoteFigure, Pattern, PatternCell } from '../types';

export function createDefaultSlot(): BeatSlot {
  return {
    active: true,
    figure: 'negra',
    span: 1,
    cells: [{ note: 'rest' }],
    subdivideKey: 0,
  };
}

export function beatSpan(figure: NoteFigure, beatsPerMeasure: number): number {
  switch (figure) {
    case 'redonda':
      return beatsPerMeasure;
    case 'blanca':
      return 2;
    default:
      return 1;
  }
}

/** Subdivisions inside one quarter-note pulse */
export function subdivisionsPerPulse(figure: NoteFigure): number {
  switch (figure) {
    case 'semicorchea':
      return 4;
    case 'corchea':
      return 2;
    case 'negra':
      return 1;
    case 'blanca':
    case 'redonda':
      return 1;
  }
}

export function cellCountForFigure(figure: NoteFigure, span: number): number {
  if (figure === 'blanca' || figure === 'redonda') {
    return span;
  }
  return span * subdivisionsPerPulse(figure);
}

export function createEmptyMeasure(beatsPerMeasure: number): Measure {
  return {
    slots: Array.from({ length: beatsPerMeasure }, () => createDefaultSlot()),
  };
}

export function createEmptyPattern(
  measureCount: number,
  beatsPerMeasure: number,
): Pattern {
  return {
    beatsPerMeasure,
    measures: Array.from({ length: measureCount }, () =>
      createEmptyMeasure(beatsPerMeasure),
    ),
  };
}

function normalizeSlots(slots: BeatSlot[], beatsPerMeasure: number): BeatSlot[] {
  const next = Array.from({ length: beatsPerMeasure }, (_, i) => {
    const s = slots[i];
    if (!s) return createDefaultSlot();
    return {
      ...s,
      cells: [...s.cells],
      active: s.active ?? true,
      span: s.span ?? 1,
      figure: s.figure ?? 'negra',
      subdivideKey: s.subdivideKey ?? 0,
    };
  });

  for (let i = 0; i < beatsPerMeasure; i++) {
    const slot = next[i];
    if (!slot.active) continue;
    const span = Math.min(slot.span, beatsPerMeasure - i);
    slot.span = span;
    const count = cellCountForFigure(slot.figure, span);
    slot.cells = Array.from({ length: count }, (_, c) => slot.cells[c] ?? { note: 'rest' });
    for (let j = 1; j < span; j++) {
      next[i + j] = { ...createDefaultSlot(), active: false, cells: [] };
    }
    i += span - 1;
  }

  return next;
}

export function resizePattern(
  pattern: Pattern,
  measureCount: number,
  beatsPerMeasure: number,
): Pattern {
  const measures = Array.from({ length: measureCount }, (_, mi) => {
    const existing = pattern.measures[mi];
    if (!existing) return createEmptyMeasure(beatsPerMeasure);
    const slots = Array.from({ length: beatsPerMeasure }, (_, i) => {
      const slot = existing.slots[i];
      return slot ? { ...slot, cells: [...slot.cells] } : createDefaultSlot();
    });
    return { slots: normalizeSlots(slots, beatsPerMeasure) };
  });
  return { beatsPerMeasure, measures };
}

export function removeMeasureAt(pattern: Pattern, measureIndex: number): Pattern {
  if (pattern.measures.length <= 1) return pattern;
  const measures = pattern.measures
    .filter((_, i) => i !== measureIndex)
    .map((m) => ({
      slots: m.slots.map((s) => ({ ...s, cells: [...s.cells] })),
    }));
  return resizePattern({ ...pattern, measures }, measures.length, pattern.beatsPerMeasure);
}

/** Aplica figura a un tiempo: subdivide con animación y puede fusionar tiempos siguientes */
export function applyFigureToBeat(
  measure: Measure,
  beatIndex: number,
  figure: NoteFigure,
  beatsPerMeasure: number,
): Measure {
  let slots = measure.slots.map((s) => ({
    ...s,
    cells: [...s.cells],
  }));

  while (slots.length < beatsPerMeasure) {
    slots.push(createDefaultSlot());
  }

  if (!slots[beatIndex].active) {
    for (let j = beatIndex - 1; j >= 0; j--) {
      if (slots[j].active && j + slots[j].span > beatIndex) {
        beatIndex = j;
        break;
      }
    }
  }

  for (let j = 0; j < beatIndex; j++) {
    if (slots[j].active && j + slots[j].span > beatIndex) {
      const newSpan = beatIndex - j;
      slots[j] = {
        ...slots[j],
        span: newSpan,
        figure: 'negra',
        cells: slots[j].cells.slice(0, newSpan).concat(
          Array.from({ length: Math.max(0, newSpan - slots[j].cells.length) }, () => ({
            note: 'rest' as const,
          })),
        ),
      };
      for (let k = j + 1; k < beatIndex; k++) {
        slots[k] = createDefaultSlot();
      }
    }
  }

  const prevCells = slots[beatIndex].cells;
  for (let i = beatIndex; i < beatsPerMeasure; i++) {
    slots[i] = createDefaultSlot();
  }

  const span = Math.min(beatSpan(figure, beatsPerMeasure), beatsPerMeasure - beatIndex);
  const cellCount = cellCountForFigure(figure, span);
  const cells: PatternCell[] = Array.from({ length: cellCount }, (_, i) => prevCells[i] ?? {
    note: 'rest',
  });

  slots[beatIndex] = {
    active: true,
    figure,
    span,
    cells,
    subdivideKey: (measure.slots[beatIndex]?.subdivideKey ?? 0) + 1,
  };

  for (let i = 1; i < span; i++) {
    slots[beatIndex + i] = { ...createDefaultSlot(), active: false, cells: [] };
  }

  return { slots: normalizeSlots(slots, beatsPerMeasure) };
}

export interface PlaybackEvent {
  globalIndex: number;
  measureIndex: number;
  cell: PatternCell;
  durationMs: number;
}

export function flattenPlayback(pattern: Pattern, bpm: number): PlaybackEvent[] {
  const quarterMs = (60 / bpm) * 1000;
  const events: PlaybackEvent[] = [];
  let g = 0;

  for (let mi = 0; mi < pattern.measures.length; mi++) {
    const measure = pattern.measures[mi];
    for (let bi = 0; bi < measure.slots.length; bi++) {
      const slot = measure.slots[bi];
      if (!slot.active) continue;
      const durationMs = (quarterMs * slot.span) / slot.cells.length;
      for (const cell of slot.cells) {
        events.push({ globalIndex: g++, measureIndex: mi, cell, durationMs });
      }
    }
  }

  return events;
}

export function globalCellIndex(
  measureIndex: number,
  beatIndex: number,
  cellIndex: number,
  pattern: Pattern,
): number {
  let g = 0;
  for (let m = 0; m < measureIndex; m++) {
    for (const slot of pattern.measures[m].slots) {
      if (slot.active) g += slot.cells.length;
    }
  }
  for (let b = 0; b < beatIndex; b++) {
    const slot = pattern.measures[measureIndex].slots[b];
    if (slot?.active) g += slot.cells.length;
  }
  return g + cellIndex;
}

export function clonePattern(pattern: Pattern): Pattern {
  return {
    beatsPerMeasure: pattern.beatsPerMeasure,
    measures: pattern.measures.map((m) => ({
      slots: m.slots.map((s) => ({
        ...s,
        cells: s.cells.map((c) => ({ ...c })),
      })),
    })),
  };
}

export function patternHasNotes(pattern: Pattern): boolean {
  return pattern.measures.some((m) =>
    m.slots.some((s) => s.active && s.cells.some((c) => c.note !== 'rest')),
  );
}
